import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import CourseCard from "../components/CourseCard";
import HeroCarousel from "../components/HeroCarousel";
import Marquee from "../components/Marquee";
import Reveal from "../components/Reveal";
import CountUp from "../components/CountUp";
import CodeRain from "../components/CodeRain";
import Typewriter from "../components/Typewriter";
import Modal from "../components/Modal";
import LeadCapture from "../components/LeadCapture";
import { Analytics } from "../lib/analytics";
import { KIDS_CATEGORIES } from "../data/kidsCategories";
import {
  Code2, Workflow, ShieldCheck, Bot, Zap, CircuitBoard, Plug, Sigma, BookOpen, Languages,
  ArrowRight, Sparkles, Calculator, FlaskConical, Palette, CheckCircle2, GraduationCap,
  Shield, BarChart2, Users, Award, Star, Mail, MapPin, Heart, MessageSquare,
  ChevronDown, ChevronUp, Briefcase, Baby, UserCheck, Send, Plus, Phone, Quote,
  ChevronLeft, ChevronRight,
} from "lucide-react";

const ICONS = { Code2, Workflow, ShieldCheck, Bot, Zap, CircuitBoard, Plug, Sigma, BookOpen, Languages };
const KIDS_ICONS = { Code2, CircuitBoard, Calculator, Languages, FlaskConical, Palette };

// ─── Homepage stats counters (fallback values used until API responds) ────────
const STATS_FALLBACK = [
  { key: "students_trained",   suffix: "+", label: "Students trained",    fallback: 1200 },
  { key: "certificates_issued",suffix: "+", label: "Certificates issued", fallback: 890  },
  { key: "courses_available",  suffix: "",  label: "Courses live",        fallback: 47   },
  { key: "countries_reached",  suffix: "",  label: "Countries reached",   fallback: 32   },
  { key: null,                 suffix: "+", label: "Corporate clients",   fallback: 15   },
];

const CAT_ACCENT = {
  programming: "#722F37", devops: "#2C497F", qa: "#2E5A44", robotics: "#8B2635",
  energy: "#B8860B", electronics: "#722F37", electrical: "#2C497F",
  mathematics: "#2E5A44", english: "#8B2635", languages: "#B8860B",
};

// ─── Who is this for ─────────────────────────────────────────────────────────
const AUDIENCES = [
  {
    icon: Baby,
    label: "Children",
    age: "Ages 4–14",
    color: "#C5A059",
    border: "border-eduhu-gold/40",
    bg: "bg-eduhu-gold/5",
    desc: "Fun, safe, interactive courses in coding, robotics, maths and sciences — taught by real educators in small groups.",
    cta: "Enroll a child",
  },
  {
    icon: GraduationCap,
    label: "Students & Teens",
    age: "Ages 14–24",
    color: "#2C497F",
    border: "border-blue-800/30",
    bg: "bg-blue-50/50",
    desc: "Build portfolio-ready skills in programming, DevOps and engineering. Stand out for university and your first job.",
    cta: "Start learning",
  },
  {
    icon: Briefcase,
    label: "Professionals",
    age: "Ages 25–55",
    color: "#722F37",
    border: "border-eduhu-claret/30",
    bg: "bg-eduhu-claret/5",
    desc: "Live, cohort-based upskilling taught by industry practitioners. Verifiable certificates recognised by employers.",
    cta: "Advance your career",
  },
  {
    icon: Heart,
    label: "Adults & Seniors",
    age: "Ages 55+",
    color: "#2E5A44",
    border: "border-green-800/30",
    bg: "bg-green-50/50",
    desc: "Never stop learning. Languages, communication, digital literacy and more — patient instructors, flexible pace.",
    cta: "Explore courses",
  },
];

// ─── Trust pillars ────────────────────────────────────────────────────────────
const TRUST_PILLARS = [
  { icon: GraduationCap, title: "Qualified instructors",     desc: "All teachers are vetted professionals with verified backgrounds and real industry experience." },
  { icon: Users,         title: "Small cohorts",             desc: "Maximum 8 students per live session — every learner gets individual attention." },
  { icon: Award,         title: "Verifiable certificates",   desc: "Each certificate has a unique ID — anyone can verify it on our public verification page." },
  { icon: BarChart2,     title: "Tracked progress",          desc: "Regular reports covering attendance, quiz scores, and milestones for every enrolled learner." },
  { icon: Shield,        title: "Safe environment",          desc: "Moderated video sessions — no external participants, no ads, no data sold to third parties." },
];


// ─── FAQ ─────────────────────────────────────────────────────────────────────
const FAQS = [
  { q: "Who is EDUHUB for?", a: "EDUHUB is for everyone — from children aged 4 to seniors aged 80+. We offer dedicated Kids Academy courses for young learners and professional courses for teens, adults and career changers." },
  { q: "How do live sessions work?", a: "You enroll, then book available time slots on your student dashboard. Sessions happen via our built-in video classroom in small cohorts of up to 8 students." },
  { q: "Are EDUHUB certificates recognised?", a: "Every EDUHUB certificate includes a unique 12-character ID that anyone can verify on our public verification page. Certificates are issued by the course instructor after completion." },
  { q: "Can I enroll my child and myself in different courses?", a: "Yes — create separate accounts or contact us for family enrollment options. Each student has their own dashboard, progress tracker and certificates." },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org", "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

// ─── Star rating display ──────────────────────────────────────────────────────
function Stars({ n }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={13} fill={i <= n ? "#C5A059" : "none"} stroke={i <= n ? "#C5A059" : "#9ca3af"} />
      ))}
    </div>
  );
}

// ─── FAQ accordion ────────────────────────────────────────────────────────────
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-eduhu-border">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left gap-4"
      >
        <span className="font-serif text-lg text-eduhu-dark">{q}</span>
        {open ? <ChevronUp size={16} className="text-eduhu-inkSoft shrink-0" /> : <ChevronDown size={16} className="text-eduhu-inkSoft shrink-0" />}
      </button>
      {open && <p className="pb-5 text-eduhu-inkSoft leading-relaxed text-sm">{a}</p>}
    </div>
  );
}

// ─── Review submission form ───────────────────────────────────────────────────
function ReviewForm({ onSubmitted }) {
  const [form, setForm] = useState({ rating: 5, text: "", course_name: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/reviews", form);
      setDone(true);
      onSubmitted?.();
    } catch (er) {
      setErr(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) return (
    <div className="flex items-center gap-2 text-eduhu-success text-sm">
      <CheckCircle2 size={16} /> Thank you! Your review is pending approval.
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm text-eduhu-inkSoft">Your rating:</span>
        {[1,2,3,4,5].map((n) => (
          <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })}>
            <Star size={20} fill={n <= form.rating ? "#C5A059" : "none"} stroke={n <= form.rating ? "#C5A059" : "#9ca3af"} />
          </button>
        ))}
      </div>
      <input
        placeholder="Course name (optional)"
        value={form.course_name}
        onChange={(e) => setForm({ ...form, course_name: e.target.value })}
        className="w-full bg-eduhu-bg2 border border-eduhu-border px-4 py-2.5 text-sm focus:outline-none focus:border-eduhu-dark"
      />
      <textarea
        required
        minLength={10}
        maxLength={500}
        placeholder="Share your experience with EDUHUB…"
        rows={3}
        value={form.text}
        onChange={(e) => setForm({ ...form, text: e.target.value })}
        className="w-full bg-eduhu-bg2 border border-eduhu-border px-4 py-2.5 text-sm focus:outline-none focus:border-eduhu-dark resize-none"
      />
      {err && <div className="text-xs text-eduhu-error">{err}</div>}
      <button disabled={busy} className="eduhu-btn-primary flex items-center gap-2">
        <Send size={14} /> {busy ? "Sending…" : "Submit review"}
      </button>
    </form>
  );
}

// ─── Reviews carousel ────────────────────────────────────────────────────────

function ReviewsCarousel({ reviews, user }) {
  const [idx, setIdx]         = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const timerRef              = useRef(null);
  const total                 = reviews.length;

  const goTo = (i) => {
    setIdx((i + total) % total);
    // reset auto-advance so manual nav doesn't immediately skip
    clearInterval(timerRef.current);
    if (total > 1) {
      timerRef.current = setInterval(
        () => setIdx((cur) => (cur + 1) % total),
        6000,
      );
    }
  };

  useEffect(() => {
    if (total <= 1) return;
    timerRef.current = setInterval(
      () => setIdx((cur) => (cur + 1) % total),
      6000,
    );
    return () => clearInterval(timerRef.current);
  }, [total]);

  // Three visible slots: prev (faded), active, next (faded)
  const slots = total > 0
    ? [
        reviews[(idx - 1 + total) % total],
        reviews[idx],
        reviews[(idx + 1) % total],
      ]
    : [];

  return (
    <section className="py-28 border-b border-eduhu-border" data-testid="reviews-section">
      {/* Header */}
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 mb-12">
        <Reveal>
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6">
            <div>
              <div className="overline text-eduhu-claret mb-3">What our students say</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-eduhu-dark">
                Real people.<br />
                <span className="italic text-eduhu-claret">Real results.</span>
              </h2>
            </div>
            {user ? (
              <button
                onClick={() => setModalOpen(true)}
                className="eduhu-btn-ghost flex items-center gap-2 shrink-0"
              >
                <MessageSquare size={14} /> Write a review
              </button>
            ) : (
              <Link to="/login" className="eduhu-btn-ghost text-sm shrink-0">
                Sign in to review
              </Link>
            )}
          </div>
        </Reveal>
      </div>

      {total === 0 ? (
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 text-center text-eduhu-inkSoft text-sm py-8">
          No reviews yet — be the first to share your experience.
        </div>
      ) : (
        <>
          {/* Carousel track */}
          <div className="relative overflow-hidden">
            {/* Desktop: 3-slot layout */}
            <div className="hidden lg:grid grid-cols-3 max-w-[1600px] mx-auto px-6 lg:px-12 gap-px bg-eduhu-border border-y border-eduhu-border">
              {slots.map((r, slotIdx) => {
                const isActive = slotIdx === 1;
                return (
                  <div
                    key={`slot-${slotIdx}`}
                    className={`bg-eduhu-bg p-8 flex flex-col gap-5 transition-opacity duration-300 ${
                      isActive ? "opacity-100" : "opacity-40 pointer-events-none"
                    }`}
                  >
                    <Stars n={r.rating} />
                    <Quote size={22} className="text-eduhu-gold/30" strokeWidth={1.2} aria-hidden="true" />
                    <p className={`leading-relaxed flex-1 ${isActive ? "text-eduhu-dark text-base" : "text-eduhu-inkSoft text-sm"}`}>
                      "{r.text}"
                    </p>
                    <div className="flex items-center gap-3 pt-4 border-t border-eduhu-border mt-auto">
                      {r.photo_url ? (
                        <img
                          src={r.photo_url}
                          alt={r.user_name}
                          className="w-10 h-10 object-cover shrink-0"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-eduhu-dark text-eduhu-gold font-serif flex items-center justify-center text-sm shrink-0">
                          {(r.user_name || "?").charAt(0)}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-eduhu-dark text-sm">{r.user_name}</div>
                        {r.course_name && (
                          <div className="text-xs text-eduhu-inkSoft">{r.course_name}</div>
                        )}
                        {r.country && (
                          <div className="text-xs text-eduhu-inkSoft">{r.country}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Mobile: single active card */}
            <div className="lg:hidden max-w-[1600px] mx-auto px-6">
              {(() => {
                const r = reviews[idx];
                return (
                  <div className="bg-eduhu-bg border border-eduhu-border p-8 flex flex-col gap-5">
                    <Stars n={r.rating} />
                    <Quote size={22} className="text-eduhu-gold/30" strokeWidth={1.2} aria-hidden="true" />
                    <p className="text-eduhu-dark leading-relaxed flex-1">"{r.text}"</p>
                    <div className="flex items-center gap-3 pt-4 border-t border-eduhu-border mt-auto">
                      {r.photo_url ? (
                        <img src={r.photo_url} alt={r.user_name} className="w-10 h-10 object-cover shrink-0" loading="lazy" />
                      ) : (
                        <div className="w-10 h-10 bg-eduhu-dark text-eduhu-gold font-serif flex items-center justify-center text-sm shrink-0">
                          {(r.user_name || "?").charAt(0)}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-eduhu-dark text-sm">{r.user_name}</div>
                        {r.course_name && <div className="text-xs text-eduhu-inkSoft">{r.course_name}</div>}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Controls: prev / dots / next */}
          {total > 1 && (
            <div className="flex items-center justify-center gap-6 mt-8">
              <button
                onClick={() => goTo(idx - 1)}
                aria-label="Previous testimonial"
                className="w-10 h-10 border border-eduhu-border flex items-center justify-center hover:bg-eduhu-bg2 transition-colors"
              >
                <ChevronLeft size={16} aria-hidden="true" />
              </button>

              <div className="flex items-center gap-2" aria-label="Testimonial slides">
                {reviews.map((_, i) => (
                  <button
                    key={i}
                    aria-current={i === idx ? "true" : undefined}
                    aria-label={`Go to review ${i + 1}`}
                    onClick={() => goTo(i)}
                    className={`h-1.5 transition-all duration-300 ${
                      i === idx ? "w-6 bg-eduhu-claret" : "w-2 bg-eduhu-border hover:bg-eduhu-inkSoft"
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={() => goTo(idx + 1)}
                aria-label="Next testimonial"
                className="w-10 h-10 border border-eduhu-border flex items-center justify-center hover:bg-eduhu-bg2 transition-colors"
              >
                <ChevronRight size={16} aria-hidden="true" />
              </button>
            </div>
          )}
        </>
      )}

      {/* Review modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        eyebrow="Your experience"
        title="Leave a review"
        maxWidth="max-w-lg"
      >
        {submitted ? (
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <CheckCircle2 size={40} className="text-eduhu-success" />
            <p className="font-serif text-2xl text-eduhu-dark">Thank you!</p>
            <p className="text-eduhu-inkSoft text-sm">Your review is pending approval and will appear shortly.</p>
            <button onClick={() => { setModalOpen(false); setSubmitted(false); }} className="eduhu-btn-primary mt-2">
              Close
            </button>
          </div>
        ) : (
          <ReviewForm onSubmitted={() => setSubmitted(true)} />
        )}
      </Modal>
    </section>
  );
}

// ─── Landing ──────────────────────────────────────────────────────────────────
export default function Landing() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [platformStats, setPlatformStats] = useState(null);
  const [consultOpen, setConsultOpen] = useState(false);
  const [syllabusOpen, setSyllabusOpen] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || []));
    api.get("/courses").then((r) => setFeatured((r.data || []).slice(0, 6)));
    api.get("/reviews").then((r) => setReviews(r.data || [])).catch(() => {});
    api.get("/stats").then((r) => setPlatformStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-schema";
    script.text = JSON.stringify(FAQ_SCHEMA);
    document.head.appendChild(script);
    return () => { const el = document.getElementById("faq-schema"); if (el) el.remove(); };
  }, []);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "keywords";
    meta.content =
      "online academy, kids coding, adult learning, professional upskilling, " +
      "seniors education, live classes, small cohorts, eduhub, " +
      "programming courses, robotics, devops, languages, STEM for kids";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  // Per-page SEO: title + OG overrides
  useEffect(() => {
    document.title = "EDUHUB — Live Academy for All Ages | Kids, Adults & Professionals";
    const metas = [
      { property: "og:title", content: "EDUHUB — Live Academy for Everyone" },
      { property: "og:description", content: "Live, small-cohort classes for children aged 4 to seniors 70+. Programming, DevOps, Robotics, Languages and more. Real instructors. Verifiable certificates." },
      { name: "description", content: "EDUHUB offers live small-cohort classes for all ages — from kids aged 4 to professionals and seniors. 47+ courses in programming, robotics, DevOps, languages and more." },
    ];
    const added = metas.map((attrs) => {
      const existing = attrs.property
        ? document.querySelector(`meta[property="${attrs.property}"]`)
        : document.querySelector(`meta[name="${attrs.name}"]`);
      if (existing) { existing.setAttribute("content", attrs.content); return null; }
      const m = document.createElement("meta");
      Object.entries(attrs).forEach(([k, v]) => m.setAttribute(k, v));
      document.head.appendChild(m);
      return m;
    });
    return () => added.forEach((m) => m && m.remove());
  }, []);

  // AggregateRating schema — built from loaded reviews
  useEffect(() => {
    if (!reviews.length) return;
    const avg = reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length;
    const schema = {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      "name": "EDUHUB Academy",
      "url": "https://eduhubcenter.com",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": avg.toFixed(1),
        "reviewCount": reviews.length,
        "bestRating": "5",
        "worstRating": "1",
      },
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "aggregate-rating-schema";
    el.text = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => { const s = document.getElementById("aggregate-rating-schema"); if (s) s.remove(); };
  }, [reviews]);

  return (
    <div data-testid="landing-page">

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <section className="relative grain border-b border-eduhu-border overflow-hidden">
        <CodeRain density={0.6} className="opacity-60" />
        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 pt-16 pb-16 lg:pt-24 lg:pb-20 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 fade-up">
            <div className="overline text-eduhu-claret mb-6">EDUHUB — A SCHOOL FOR EVERYONE</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight text-eduhu-dark">
              {t("hero.title.line1")}
              <br />
              <span className="italic text-eduhu-claret">{t("hero.title.line2")}</span>
            </h1>
            <div className="mt-6 text-eduhu-inkSoft font-mono text-base flex flex-col sm:flex-row sm:items-baseline">
              <span>
                <span className="text-eduhu-gold">▍ </span>
                <span className="text-eduhu-dark">Now teaching: </span>
              </span>
              <Typewriter
                phrases={[
                  "Python for beginners",
                  "ROS 2 Robotics",
                  "Kubernetes in production",
                  "Calculus for adults",
                  "English for seniors",
                  "Scratch for kids aged 6",
                  "QA automation",
                  "Solar power engineering",
                  "C++ systems",
                  "Junior robotics, ages 8–12",
                ]}
                className="text-eduhu-claret"
              />
            </div>
            <p className="mt-8 text-lg max-w-xl text-eduhu-inkSoft leading-relaxed">
              Live, small-cohort tutorials taught by real practitioners — for children, professionals, career changers and seniors.
              From Python to power systems, from kids coding to communication skills.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-4">
              <Link data-testid="hero-cta-courses" to="/courses" className="eduhu-btn-primary pulse-gold">
                Browse courses <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <div className="flex items-center gap-3">
                <button
                  data-testid="hero-cta-consultation"
                  onClick={() => { setConsultOpen(true); Analytics.consultationClick("hero"); }}
                  className="eduhu-btn-gold !py-[0.7rem] !px-4 text-sm"
                >
                  <Phone size={14} strokeWidth={1.5} /> Book free consultation
                </button>
                <Link data-testid="hero-cta-playground" to="/playground" className="eduhu-btn-ghost !py-[0.7rem] !px-4 text-sm">
                  <Sparkles size={15} className="floaty" /> Try the playground
                </Link>
              </div>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-10 gap-y-4 text-sm text-eduhu-inkSoft">
              <div><div className="font-serif text-4xl text-eduhu-dark"><CountUp to={47} /></div><div className="overline">courses live</div></div>
              <div><div className="font-serif text-4xl text-eduhu-dark"><CountUp to={10} /></div><div className="overline">disciplines</div></div>
              <div><div className="font-serif text-4xl text-eduhu-dark"><CountUp to={4} /></div><div className="overline">age groups</div></div>
              <div><div className="font-serif text-4xl text-eduhu-dark">∞</div><div className="overline">small cohorts</div></div>
            </div>
          </div>
          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "120ms" }}>
            <HeroCarousel />
          </div>
        </div>
      </section>

      {/* ── MARQUEE ────────────────────────────────────────────────────────── */}
      <Marquee />

      {/* ── PLATFORM STATISTICS ───────────────────────────────────────────── */}
      <section className="border-y border-eduhu-border bg-eduhu-dark text-eduhu-bg grain" data-testid="stats-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-white/5">
            {STATS_FALLBACK.map((s, idx) => {
              const live = s.key && platformStats ? platformStats[s.key] : null;
              const value = (live != null && live > 0) ? live : s.fallback;
              return (
                <Reveal key={s.label} delay={idx * 70} className="bg-eduhu-dark">
                  <div className="py-10 px-6 text-center">
                    <div className="font-serif text-5xl lg:text-6xl text-eduhu-gold">
                      <CountUp to={value} suffix={s.suffix} />
                    </div>
                    <div className="overline mt-3 text-eduhu-bg/60 text-xs">{s.label}</div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── WHO IS THIS FOR ───────────────────────────────────────────────── */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24" data-testid="audiences-section">
        <Reveal>
          <div className="text-center mb-14">
            <div className="overline text-eduhu-claret mb-3">An academy for every learner</div>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-eduhu-dark">
              Whoever you are,<br />
              <span className="italic text-eduhu-claret">there is a course for you.</span>
            </h2>
            <p className="mt-5 text-eduhu-inkSoft max-w-2xl mx-auto leading-relaxed">
              EDUHUB serves learners of all ages and backgrounds — from parents enrolling their 4-year-old in Junior Coding to
              a 67-year-old discovering English Communication. Every class is live, every cohort is small.
            </p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-eduhu-border border border-eduhu-border">
          {AUDIENCES.map((a, idx) => {
            const Icon = a.icon;
            return (
              <Reveal key={a.label} delay={idx * 80} className="bg-eduhu-bg h-full">
                <div className={`p-8 flex flex-col gap-4 h-full relative overflow-hidden group hover:${a.bg} transition-colors`}>
                  <span className="absolute top-0 left-0 h-1 w-12 transition-all duration-500 group-hover:w-full" style={{ background: a.color }} />
                  <div className="w-12 h-12 flex items-center justify-center border" style={{ borderColor: a.color + "50" }}>
                    <Icon size={22} strokeWidth={1.4} style={{ color: a.color }} />
                  </div>
                  <div>
                    <div className="font-serif text-2xl text-eduhu-dark leading-tight">{a.label}</div>
                    <div className="text-xs uppercase tracking-widest mt-1" style={{ color: a.color }}>{a.age}</div>
                  </div>
                  <p className="text-sm text-eduhu-inkSoft leading-relaxed flex-1">{a.desc}</p>
                  <Link to="/courses" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest transition-colors" style={{ color: a.color }}>
                    {a.cta} <ArrowRight size={11} />
                  </Link>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ── PROFESSIONAL DISCIPLINES ──────────────────────────────────────── */}
      <section className="border-y border-eduhu-border bg-eduhu-bg2 py-24" data-testid="categories-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="overline text-eduhu-claret mb-3">Professional courses</div>
                <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-eduhu-dark">
                  Ten disciplines.<br />One school.
                </h2>
              </div>
              <Link to="/courses" className="hidden lg:inline-flex eduhu-btn-ghost !py-2 !px-4 text-sm">
                All courses <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-eduhu-border border border-eduhu-border">
            {categories.map((c, idx) => {
              const Icon = ICONS[c.icon] || Code2;
              const accent = CAT_ACCENT[c.id] || "#722F37";
              return (
                <Reveal key={c.id} delay={(idx % 5) * 80}>
                  <Link
                    to={`/courses?category=${c.id}`}
                    data-testid={`cat-card-${c.id}`}
                    className="bg-eduhu-bg p-7 flex flex-col gap-4 hover:bg-eduhu-bg3 transition-colors group relative overflow-hidden h-full"
                  >
                    <span className="absolute top-0 left-0 h-1 w-12 transition-all duration-500 group-hover:w-full" style={{ background: accent }} />
                    <Icon size={28} strokeWidth={1.3} className="transition-transform duration-500 group-hover:scale-110" style={{ color: accent }} />
                    <div className="font-serif text-xl text-eduhu-dark leading-tight">
                      {lang === "ro" && c.name_ro ? c.name_ro : c.name}
                    </div>
                    <div className="text-xs text-eduhu-inkSoft leading-relaxed line-clamp-3">{c.description}</div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
          <Reveal>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4 text-center">
              <p className="text-eduhu-inkSoft text-sm w-full">Not sure which course fits you? Get the full syllabus emailed to you.</p>
              <button
                data-testid="disciplines-cta-syllabus"
                onClick={() => { setSyllabusOpen(true); Analytics.syllabusRequest(); }}
                className="eduhu-btn-ghost"
              >
                <BookOpen size={15} strokeWidth={1.5} /> Request a syllabus
              </button>
              <button
                onClick={() => { setConsultOpen(true); Analytics.consultationClick("disciplines"); }}
                className="eduhu-btn-ghost"
              >
                <Phone size={15} strokeWidth={1.5} /> Book free consultation
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FEATURED COURSES ──────────────────────────────────────────────── */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24" data-testid="featured-section">
        <Reveal>
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="overline text-eduhu-claret mb-3">Hand-picked</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-eduhu-dark">
                Popular this term.
              </h2>
            </div>
            <Link to="/courses" className="hidden lg:inline-flex eduhu-btn-ghost !py-2 !px-4 text-sm">
              All 47 courses <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-eduhu-border border border-eduhu-border">
          {featured.map((c, idx) => (
            <Reveal key={c.id} delay={(idx % 3) * 100}>
              <CourseCard course={c} />
            </Reveal>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/courses" className="eduhu-btn-primary">
            Explore all 47 courses <ArrowRight size={16} strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* ── KIDS ACADEMY (compact) ────────────────────────────────────────── */}
      <section className="border-y border-eduhu-border bg-eduhu-dark text-eduhu-bg grain py-24" data-testid="kids-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="overline text-eduhu-gold mb-3">Kids Academy — Ages 4 to 14</div>
                <h2 className="font-serif text-4xl lg:text-5xl leading-tight">
                  Six disciplines.<br />
                  <span className="italic text-eduhu-gold">Built for curious young minds.</span>
                </h2>
                <p className="mt-4 text-eduhu-bg/70 max-w-lg text-sm leading-relaxed">
                  Safe, moderated video classrooms. Max 8 children per session. Parent progress updates after every class.
                </p>
              </div>
              <Link to="/kids-academy" className="hidden lg:inline-flex eduhu-btn-gold !py-2 !px-4 text-sm shrink-0">
                Kids courses <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-eduhu-gold/10 border border-eduhu-gold/20">
            {KIDS_CATEGORIES.map((c, idx) => {
              const Icon = KIDS_ICONS[c.icon] || Code2;
              return (
                <Reveal key={c.id} delay={(idx % 6) * 70}>
                  <Link
                    to={`/kids-academy?category=${c.id}`}
                    data-testid={`kids-cat-${c.id}`}
                    className="bg-eduhu-darkSoft p-6 flex flex-col gap-3 hover:border-eduhu-gold/50 transition-colors group relative overflow-hidden h-full border border-transparent"
                  >
                    <span className="absolute top-0 left-0 h-1 w-10 transition-all duration-500 group-hover:w-full" style={{ background: c.color }} />
                    <Icon size={24} strokeWidth={1.3} className="transition-transform duration-500 group-hover:scale-110 mt-1" style={{ color: c.color }} />
                    <div className="font-serif text-lg text-eduhu-bg leading-tight">{c.name}</div>
                    <div className="text-xs text-eduhu-bg/60 leading-relaxed">{c.desc}</div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-10 flex flex-wrap gap-3 items-center justify-between">
            <div className="flex flex-wrap gap-3">
              {["Safe Online Classrooms", "Small Cohorts", "Parent Progress Updates", "Real Certificates"].map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5 border border-eduhu-gold/30 text-eduhu-bg/80 text-xs px-3 py-1.5">
                  <CheckCircle2 size={11} className="text-eduhu-gold" /> {b}
                </span>
              ))}
            </div>
            <Link to="/register" className="eduhu-btn-gold pulse-gold">
              Enroll your child <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── PLAYGROUND TEASER ─────────────────────────────────────────────── */}
      <section className="border-b border-eduhu-border bg-eduhu-bg2">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="overline text-eduhu-claret mb-3">In-browser code playground</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] text-eduhu-dark">
              Type. Run. Learn.<br />
              <span className="italic text-eduhu-claret">No install required.</span>
            </h2>
            <p className="mt-5 text-eduhu-inkSoft max-w-md">
              Python, JavaScript, C, C++ — sandboxed execution built into every lesson. Try it before you enroll.
            </p>
            <Link to="/playground" className="eduhu-btn-primary mt-8 inline-flex">
              Open the playground <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
          <div className="lg:col-span-6">
            <div className="border border-eduhu-border bg-eduhu-dark p-5 font-mono text-sm leading-relaxed text-eduhu-bg/90">
              <div className="text-eduhu-gold mb-2">▍ playground · python</div>
              <pre className="whitespace-pre-wrap">{`def fib(n):
    a, b = 0, 1
    for _ in range(n):
        a, b = b, a + b
    return a

print([fib(i) for i in range(10)])
# → [0, 1, 1, 2, 3, 5, 8, 13, 21, 34]`}</pre>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────────────────────── */}
      <section id="how" className="max-w-[1600px] mx-auto px-6 lg:px-12 py-28" data-testid="how-section">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="overline text-eduhu-claret mb-3">How it works</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-eduhu-dark leading-tight">
                A school that<br />
                <span className="italic">respects your time.</span>
              </h2>
              <p className="mt-6 text-eduhu-inkSoft leading-relaxed max-w-md">
                Every cohort is small. Every teacher is vetted. You pay once, you learn live, you leave with proof.
              </p>
              <div className="mt-8 aspect-[5/6] overflow-hidden border border-eduhu-border">
                <img
                  src="https://images.unsplash.com/photo-1736066330610-c102cab4e942?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Professor teaching"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ol className="divide-y divide-eduhu-border">
              {[
                { n: "01", title: "Browse & choose", body: "Explore 47+ courses across 10 disciplines. Filter by age group, level and category to find exactly the right course." },
                { n: "02", title: "Register & enroll", body: "Create a free account, then enroll with a single payment. Apply a voucher or referral code for a discount." },
                { n: "03", title: "Book live sessions", body: "From your dashboard, book available live slots with your teacher. Sessions are small — max 8 students." },
                { n: "04", title: "Learn & certify", body: "Attend live video sessions, complete quizzes, and receive a verifiable digital certificate on completion." },
              ].map((s, i) => (
                <li key={s.n} className="py-7">
                  <Reveal delay={i * 80} className="grid grid-cols-12 gap-6 items-start">
                    <div className="col-span-2 font-serif text-5xl text-eduhu-gold">{s.n}</div>
                    <div className="col-span-10">
                      <h3 className="font-serif text-2xl text-eduhu-dark mb-2">{s.title}</h3>
                      <p className="text-eduhu-inkSoft leading-relaxed">{s.body}</p>
                    </div>
                  </Reveal>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ── TRUST PILLARS ─────────────────────────────────────────────────── */}
      <section className="border-y border-eduhu-border bg-eduhu-bg2 py-24" data-testid="trust-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="mb-14 text-center max-w-2xl mx-auto">
              <div className="overline text-eduhu-claret mb-3">Why choose EDUHUB</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-eduhu-dark">
                Built for real learning,<br />
                <span className="italic text-eduhu-claret">not passive watching.</span>
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-eduhu-border border border-eduhu-border">
            {TRUST_PILLARS.map((item, idx) => {
              const Icon = item.icon;
              return (
                <Reveal key={item.title} delay={idx * 70}>
                  <div className="bg-eduhu-bg p-8 flex flex-col gap-4 h-full">
                    <div className="w-10 h-10 border border-eduhu-gold/40 flex items-center justify-center flex-shrink-0">
                      <Icon size={18} className="text-eduhu-gold" strokeWidth={1.4} />
                    </div>
                    <div className="font-serif text-xl text-eduhu-dark leading-tight">{item.title}</div>
                    <div className="text-sm text-eduhu-inkSoft leading-relaxed">{item.desc}</div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────────────────────────── */}
      <ReviewsCarousel reviews={reviews} user={user} />

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section className="border-y border-eduhu-border bg-eduhu-bg2 py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-center mb-12">
              <div className="overline text-eduhu-claret mb-3">Common questions</div>
              <h2 className="font-serif text-4xl tracking-tight text-eduhu-dark">
                Everything you need to know.
              </h2>
            </div>
          </Reveal>
          <div>
            {FAQS.map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* ── CONTACT + TRUST ───────────────────────────────────────────────── */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24" data-testid="contact-section">
        <div className="grid lg:grid-cols-2 gap-12">
          <Reveal>
            <div>
              <div className="overline text-eduhu-claret mb-3">Get in touch</div>
              <h2 className="font-serif text-4xl tracking-tight text-eduhu-dark">
                We're here to help<br />
                <span className="italic">at every step.</span>
              </h2>
              <p className="mt-5 text-eduhu-inkSoft leading-relaxed max-w-md">
                Whether you're enrolling a child, choosing a professional course, or have a question about our platform —
                our team responds within one business day.
              </p>
              <div className="mt-8 space-y-4">
                <a href="mailto:hello@eduhubcenter.com" className="flex items-center gap-3 group">
                  <div className="w-10 h-10 border border-eduhu-border flex items-center justify-center group-hover:bg-eduhu-dark transition-colors">
                    <Mail size={16} className="text-eduhu-gold" />
                  </div>
                  <div>
                    <div className="text-xs overline text-eduhu-inkSoft">Email us</div>
                    <div className="text-sm font-medium text-eduhu-dark group-hover:text-eduhu-claret transition-colors">hello@eduhubcenter.com</div>
                  </div>
                </a>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 border border-eduhu-border flex items-center justify-center">
                    <MapPin size={16} className="text-eduhu-gold" />
                  </div>
                  <div>
                    <div className="text-xs overline text-eduhu-inkSoft">Registered office</div>
                    <div className="text-sm font-medium text-eduhu-dark">Bucharest, Romania · Worldwide delivery</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 border border-eduhu-border flex items-center justify-center">
                    <UserCheck size={16} className="text-eduhu-gold" />
                  </div>
                  <div>
                    <div className="text-xs overline text-eduhu-inkSoft">Support hours</div>
                    <div className="text-sm font-medium text-eduhu-dark">Mon–Fri, 09:00–18:00 EET</div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="space-y-4">
              {[
                { icon: Award,         title: "Verified certificates",   desc: "Every certificate carries a unique ID. Anyone can check its authenticity instantly on our public verification page." },
                { icon: GraduationCap, title: "Expert instructors",      desc: "All teachers are vetted practitioners with real-world experience — not just academics or self-taught tutors." },
                { icon: Users,         title: "Small class sizes",       desc: "A maximum of 8 students per live session guarantees personal attention, not an anonymous crowd." },
                { icon: Shield,        title: "Safe & secure",           desc: "Secure encrypted sessions, strict moderation, and a privacy-first data policy. We never sell your information." },
                { icon: CheckCircle2,  title: "Satisfaction commitment",  desc: "Not satisfied? Contact our support team — we'll do everything we can to make it right." },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex items-start gap-4 p-5 border border-eduhu-border bg-eduhu-bg2">
                  <div className="w-9 h-9 border border-eduhu-gold/40 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon size={16} className="text-eduhu-gold" strokeWidth={1.4} />
                  </div>
                  <div>
                    <div className="font-medium text-eduhu-dark text-sm">{title}</div>
                    <div className="text-xs text-eduhu-inkSoft mt-1 leading-relaxed">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── TECHNOLOGIES WE TEACH ────────────────────────────────────────── */}
      <section className="border-b border-eduhu-border py-12 bg-eduhu-bg2" data-testid="tech-logos-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-8">
            <div className="overline text-eduhu-inkSoft">Technologies & tools covered in our curriculum</div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {[
              "Python", "JavaScript", "React", "Node.js", "Docker",
              "Kubernetes", "ROS 2", "Linux", "Git", "SQL",
              "Scratch", "C++", "TypeScript", "Ansible",
            ].map((tech) => (
              <span key={tech} className="text-sm font-mono text-eduhu-inkSoft hover:text-eduhu-dark transition-colors px-3 py-1.5 border border-transparent hover:border-eduhu-border">
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── STUDENT SUCCESS STORIES ───────────────────────────────────────── */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24" data-testid="success-stories-section">
        <Reveal>
          <div className="text-center mb-14">
            <div className="overline text-eduhu-claret mb-3">Student outcomes</div>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight text-eduhu-dark">
              Real careers changed.<br />
              <span className="italic text-eduhu-claret">Real skills earned.</span>
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-eduhu-border border border-eduhu-border">
          {[
            {
              name: "Maria C.",
              before: "Retail assistant",
              after: "Junior Python developer",
              course: "Python for Professionals",
              quote: "I had zero coding experience. After 3 months at EDUHUB I landed my first dev job. The small class size made all the difference — my teacher actually knew my name.",
            },
            {
              name: "James O.",
              before: "IT support technician",
              after: "DevOps engineer",
              course: "Kubernetes in Production",
              quote: "The course was intense but practical — everything we covered I used at work the next week. Got a 40% salary increase within 6 months of completing the certificate.",
            },
            {
              name: "Elena P.",
              before: "Parent looking for kids activities",
              after: "Her son now builds robots",
              course: "Junior Robotics (ages 8–12)",
              quote: "My son was shy at first but after two sessions he was the one helping other kids. The teacher is exceptional — patient, creative, and genuinely passionate about teaching.",
            },
          ].map((s, idx) => (
            <Reveal key={s.name} delay={idx * 100}>
              <div className="bg-eduhu-bg p-8 flex flex-col gap-5 h-full">
                <Quote size={24} className="text-eduhu-gold/40" strokeWidth={1.2} aria-hidden="true" />
                <p className="text-eduhu-dark leading-relaxed text-sm flex-1">"{s.quote}"</p>
                <div className="pt-4 border-t border-eduhu-border">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-eduhu-dark text-eduhu-gold font-serif flex items-center justify-center text-sm shrink-0">
                      {s.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-medium text-sm text-eduhu-dark">{s.name}</div>
                      <div className="text-xs text-eduhu-inkSoft">{s.course}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <span className="px-2 py-1 bg-eduhu-bg2 text-eduhu-inkSoft line-through">{s.before}</span>
                    <ArrowRight size={11} className="text-eduhu-gold shrink-0" aria-hidden="true" />
                    <span className="px-2 py-1 bg-eduhu-gold/10 text-eduhu-dark font-medium">{s.after}</span>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── CTA STRIP ─────────────────────────────────────────────────────── */}
      <section className="bg-eduhu-dark text-eduhu-bg grain" data-testid="cta-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline text-eduhu-gold mb-3">Begin your study — any age, any level</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight">
              Reserve your seat<br />in the next cohort.
            </h2>
            <p className="mt-4 text-eduhu-bg/70 text-sm max-w-md">
              Registration is free. No credit card until you enroll. Browse all 47 courses first.
            </p>
          </div>
          <div className="md:text-right space-y-3">
            <div className="flex flex-wrap md:justify-end gap-3">
              <Link to="/register" className="eduhu-btn-gold pulse-gold">
                Create your account <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <Link to="/courses" className="eduhu-btn-ghost text-eduhu-bg border-eduhu-bg/30 hover:border-eduhu-bg">
                Browse courses
              </Link>
            </div>
            <div className="text-xs text-eduhu-bg/50">Questions? Email hello@eduhubcenter.com</div>
          </div>
        </div>
      </section>

      <LeadCapture variant="consultation" open={consultOpen} onClose={() => setConsultOpen(false)} />
      <LeadCapture variant="syllabus" open={syllabusOpen} onClose={() => setSyllabusOpen(false)} />

    </div>
  );
}
